// A table on a slide, drawn the same everywhere a slide shows (S7): a grid of cells in the box, its columns at their
// shares of the box's width, its rows at least their shares of its height (a row grows with its words). Its look
// comes from the deck's theme colours: the header row stands on the box's fill (the accent unless set) in the ink that
// reads there; banded rows are a quiet tint of that fill over the slide; borders are the box's line colour, or soft
// rules between rows when it sets none. A cell's own fill and ink go over those. The editor draws its own cells' words
// through `cell` (TableEdit.tsx); nothing here changes a slide.

import { createContext, type CSSProperties, type ReactNode } from "react";
import type { Palette, SlideBox } from "../../../../shared/slideDesign";
import type { TableCell } from "../../../../shared/slideTable";
import { cx } from "../../../lib/util";
import { readableOn } from "../../../themes/themes";
import { mixHex } from "../../../../shared/deckTheme";
import { BoxWords, paint, wordsStyle } from "../Boxes";
import "../../../styles/slides-table.css";
import { cellLook, cssSide, styled } from "./style";

/** The slide's colours and fonts, for a table's editor drawn inside its box. */
export const TableCtx = createContext<{ palette: Palette; theme: { title: string; body: string } } | null>(null);

/** A table's words' size when the box says none: a step under a text box's, as tables hold more. */
export const TABLE_SIZE = 26;

export interface TableLook {
  head: string;
  onHead: string;
  band: string;
  rule: string;
  grid: boolean;
  lineW: number;
  ink: string;
}

const hex6 = (c: string) => (/^#[0-9a-f]{6}$/i.test(c) ? c : "#888888");

/** The colours a table is drawn in on this slide. */
export function tableLook(b: SlideBox, palette: Palette): TableLook {
  const head = hex6(paint(b.fill ?? "accent", palette, palette.accent));
  const bg = hex6(palette.background);
  const ink = paint(b.color, palette, palette.text);
  return {
    head,
    onHead: readableOn(head) === "#ffffff" ? "#FFFFFF" : "#111317",
    band: mixHex(bg, head, 0.1),
    rule: b.line ? paint(b.line, palette, palette.text) : mixHex(bg, hex6(palette.text), 0.18),
    grid: !!b.line,
    lineW: b.line ? (b.lineW ?? 1.5) : 1.5,
    ink,
  };
}

/** A cell's fill and ink: its own over the table's (the header's, a band's). */
export function cellColors(c: TableCell, row: number, b: SlideBox, look: TableLook, palette: Palette): { fill: string | undefined; ink: string } {
  const isHead = !!b.table?.head && row === 0;
  const bodyRow = b.table?.head ? row - 1 : row;
  let fill: string | undefined = isHead ? look.head : b.table?.band && bodyRow % 2 === 1 ? look.band : undefined;
  if (c.fill === null) fill = undefined;
  else if (c.fill) fill = paint(c.fill, palette, palette.surface);
  const onOwn = fill && /^#[0-9a-f]{6}$/i.test(fill) && (c.fill || isHead) ? (readableOn(fill) === "#ffffff" ? "#FFFFFF" : "#111317") : look.ink;
  const ink = c.color ? paint(c.color, palette, look.ink) : isHead && !c.fill ? look.onHead : c.fill ? onOwn : look.ink;
  return { fill, ink };
}

/**
 * The grid. `cell` draws the words of a cell being typed in (the editor's); `active` marks the cells selected.
 */
export function TableGrid({ box: b, palette, theme, cell, active }: { box: SlideBox; palette: Palette; theme: { title: string; body: string }; cell?: (r: number, c: number) => ReactNode | undefined; active?: (r: number, c: number) => boolean }) {
  const t = b.table!;
  const look = tableLook(b, palette);
  const words = wordsStyle(b, palette, theme, { size: TABLE_SIZE, lineH: 1.25, color: look.ink });
  const style: CSSProperties = {
    fontFamily: words.fontFamily,
    fontSize: words.fontSize,
    lineHeight: words.lineHeight,
    fontWeight: words.fontWeight,
    ["--tb-rule" as string]: look.rule,
    ["--tb-line-w" as string]: `${look.lineW}px`,
  };
  // SB: a table style, a first column, or cells with their own borders: drawn cell by cell (table/style.ts)
  const own = styled(t);
  return (
    <table className={cx("sl-tb", own ? "is-own" : look.grid ? "is-grid" : b.line === null ? "is-bare" : "is-rules", t.head && "has-head")} style={style}>
      <colgroup>
        {t.cols.map((w, i) => (
          <col key={i} style={{ width: `${w * 100}%` }} />
        ))}
      </colgroup>
      <tbody>
        {t.rows.map((row, r) => (
          <tr key={r} style={{ height: row.h * b.h }} className={cx(t.head && r === 0 && "is-head")}>
            {row.cells.map((c, ci) => {
              if (c.m) return null;
              const cl = own ? cellLook(b, r, ci, palette) : null;
              const { fill, ink } = cl ?? cellColors(c, r, b, look, palette);
              const editing = cell?.(r, ci);
              return (
                <td
                  key={ci}
                  colSpan={c.cs}
                  rowSpan={c.rs}
                  data-cell={`${r}:${ci}`}
                  className={cx(active?.(r, ci) && "is-on", editing !== undefined && "is-typing")}
                  style={{ background: fill, color: ink, textAlign: c.align ?? b.align ?? "left", verticalAlign: (c.valign ?? b.valign) === "middle" ? "middle" : (c.valign ?? b.valign) === "bottom" ? "bottom" : "top", ...(cl ? { fontWeight: cl.bold ? 640 : undefined, borderTop: cssSide(cl.t), borderRight: cssSide(cl.r), borderBottom: cssSide(cl.b), borderLeft: cssSide(cl.l) } : {}) }}
                >
                  <div className="sl-tb-words">{editing ?? <BoxWords text={c.text} palette={palette} />}</div>
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
